<script lang="ts">
	import type { Device } from 'src/types/Device';

	import { format } from 'src/services/i18n';

	import { devices_settings, devices_status } from 'src/stores';
	import {
		getZWaveChannelConfigName,
		syncZWaveCloudDeviceName,
		toZWaveComponentNameValue,
		zwaveSetComponentName,
	} from 'src/helpers/z_wave_gateway_helpers';
	import { real_id } from 'src/helpers/interface_helpers';

	import { Alert, Button, Input, Text, toast } from '@Shelly/shelly-design-system';

	export let device: Device;

	let name = '';
	let cache = '';
	let loading = false;
	let seeded = false;

	$: status = $devices_status?.[real_id(device.id)];
	$: settings = $devices_settings?.[real_id(device.id)];
	$: canSaveName = Boolean(status?.online);

	/** Channel component name only (`switch:100.name`) — not zwavedevice name. */
	$: configName = getZWaveChannelConfigName(device, status, settings);
	$: if (!seeded && settings !== undefined) {
		name = configName;
		cache = configName;
		seeded = true;
	}

	$: isUnchanged = toZWaveComponentNameValue(cache) === toZWaveComponentNameValue(name);

	const saveName = async () => {
		const nextName = toZWaveComponentNameValue(name);
		const previousCache = cache;
		if (loading || !canSaveName || isUnchanged) return;

		loading = true;
		name = nextName ?? '';

		const componentResponse = await zwaveSetComponentName(device, nextName);

		if (!componentResponse?.isok) {
			name = previousCache;
			toast.open({ type: 'danger', title: $format('error_global') });
			loading = false;
			return;
		}

		const cloudOk = await syncZWaveCloudDeviceName(device, nextName);
		if (cloudOk) {
			cache = nextName ?? '';
			toast.open({
				type: 'success',
				title: nextName
					? $format('chan_name_succ', { values: { name: nextName } }) ||
						`Channel name was changed to ${nextName}`
					: $format('channel_name_cleared_scc_toast', {
							default: 'Channel name cleared.',
						}),
			});
		} else {
			name = previousCache;
			toast.open({ type: 'danger', title: $format('error_global') });
		}

		loading = false;
	};
</script>

{#if !canSaveName}
	<Alert
		class="mb-s"
		type="info"
		heading={$format('alert_message_zwave_channel_name_disabled', {
			default: 'Available only when this device is online.',
		})}
	/>
{/if}

<Input
	class="mb-s"
	icon="fas fa-microchip"
	size="m"
	label={$format('sub_settings_title_channel_name')}
	bind:value={name}
	disabled={!canSaveName}
	on:enter={saveName}
	id="input-channel-name-z"
	aria-labelledby="input-channel-name-z"
/>
<Text class="mt-s mb-s" styleType="body-small">
	{$format('zwave_channel_rename_description', {
		default:
			'Change the current channel name. Leave empty to fall back to the generated name, e.g. Z-Wave Cover (203).',
	})}
</Text>
<Button
	{loading}
	size="m"
	class="mt-s"
	width="full"
	on:click={saveName}
	disabled={!canSaveName || isUnchanged}
>
	{$format('button_save')}
</Button>
